import React, { useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import Loader from 'app/components/atoms/Loader/Loader';
import { saveComponentState } from 'store/actions/component/componentActions';
import { executeGlobalSearch } from 'store/actions/search/globalSearchActions';
import {
    buildSearchCriteria,
    getEffectiveDataTypeFromTokens,
    getLockedTypeTokenIndex,
} from 'app/utils/search/globalSearchUtils';
import { getEntityUrl } from 'app/utils/entity/entityUtils';
import { get } from 'app/utils/lo/lo';
import { getActiveUserReferences } from 'store/selectors/userSelectors';
import history from 'store/History';

import { INPUT_STATE, OPERATORS, isNullOperator } from './GlobalSearchModal.constants';
import { getFilterConfig, getAllFilterOptions, rebuildFilterRegistry, STATIC_FILTER_KEYS, TYPE_DEPENDENT_FILTER_KEYS } from './GlobalSearchModal.filterConfig';
import { useSearchState } from './hooks';
import {
    SearchInput,
    FilterList,
    OperatorList,
    FilterValueList,
    SearchResults,
    NoSearchResults,
    RecentSearches,
} from './components';
import GlobalSearchFilterInput from './GlobalSearchFilterInput';
import {
    StyledDialog,
    SearchContent,
    ResultsContainer,
    FilterOverlay,
    NoResults,
} from './GlobalSearchModal.styles';

const COMPONENT_ID = 'GlobalSearchModal';
const MAX_RECENT_SEARCHES = 5;

const GlobalSearchModal = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const inputRef = useRef(null);
    const lastLoadedIndexRef = useRef(-1); // Track last loaded startIndex to prevent duplicates
    
    // Redux state
    const entityTypes = useSelector((state) => state.app.allPrimaryClasses.records || []);
    const userReferences = useSelector(getActiveUserReferences);
    const recentSearches = useSelector((state) => 
        get(state, `component.state.${COMPONENT_ID}.recentSearches`) || []
    );
    
    // Local state
    const { state, actions, constants } = useSearchState();
    const {
        searchQuery,
        searchTokens,
        currentInputState,
        currentFilter,
        currentOperator,
        showFilterInput,
        filterInputValue,
        isLoading,
        isLoadingMore,
        searchResults,
        totalCount,
        hasMore,
        hasSearched,
        isInputFocused,
        highlightedIndex,
    } = state;
    
    const hasTypeToken = useMemo(
        () => searchTokens.some((t) => t.filter?.key === 'type'),
        [searchTokens]
    );

    const filterOptions = useMemo(() => {
        rebuildFilterRegistry(entityTypes);
        return getAllFilterOptions(hasTypeToken);
    }, [entityTypes, hasTypeToken]);
    
    const typeFilterConfig = useMemo(() => getFilterConfig('type'), []);

    // When adding a primary.* filter, ensure a Type token exists for that entity type so the type is
    // visible and locked (user cannot change it). Type filter is hidden from the list (used).
    const ensureTypeTokenForPrimaryFilter = useCallback((entityTypeUri) => {
        if (!entityTypeUri || !typeFilterConfig) return;
        const hasMatchingTypeToken = searchTokens.some(
            (t) =>
                t.filter?.key === 'type' &&
                (t.value?.type?.uri === entityTypeUri || t.value?.uri === entityTypeUri)
        );
        if (hasMatchingTypeToken) return;
        const entityType = (entityTypes || []).find((et) => et.uri === entityTypeUri);
        if (!entityType) return;
        actions.addToken(
            { key: 'type', text: typeFilterConfig.text, icon: typeFilterConfig.icon },
            null,
            { type: entityType, displayName: entityType.name }
        );
    }, [entityTypes, searchTokens, typeFilterConfig, actions]);

    const lockedTypeTokenIndex = useMemo(
        () => getLockedTypeTokenIndex(searchTokens),
        [searchTokens]
    );

    // Helpers
    const focusInput = useCallback(() => inputRef.current?.focus(), []);

    const handleRemoveToken = useCallback(
        (index) => {
            if (index === lockedTypeTokenIndex) return;
            actions.removeToken(index);
            // Re-focus input after chip removal so filter list stays visible
            setTimeout(focusInput, 0);
        },
        [lockedTypeTokenIndex, actions, focusInput]
    );
    
    const handleInputClick = useCallback(() => {
        inputRef.current?.focus();
        actions.setHighlightedIndex(-1);
    }, [actions]);
    
    const filteredOptions = useMemo(() => {
        const usedKeys = searchTokens.filter(t => t.filter?.key).map(t => t.filter.key);
        const effectiveDataType = getEffectiveDataTypeFromTokens(searchTokens);

        let available = filterOptions.filter((opt) => {
            if (usedKeys.includes(opt.key)) return false;
            // Hide type-dependent filters until a Type token is selected
            if (!hasTypeToken && TYPE_DEPENDENT_FILTER_KEYS.includes(opt.key)) return false;
            if (effectiveDataType) {
                if (STATIC_FILTER_KEYS.includes(opt.key)) return true;
                if (opt.key.startsWith('primary.')) {
                    const config = getFilterConfig(opt.key);
                    return config?.entityTypeUri === effectiveDataType;
                }
                return false;
            }
            return true;
        });

        if (searchQuery.trim()) {
            available = available.filter(opt => opt.text.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return available;
    }, [filterOptions, searchTokens, searchQuery, hasTypeToken]);
    
    const filteredValues = useMemo(() => {
        if (!currentFilter) return [];
        const config = getFilterConfig(currentFilter.key);
        if (!config) return [];
        const allValues = config.getValues({ props: { entityTypes, userReferences } });
        return config.filterValues(allValues, searchQuery, currentOperator);
    }, [currentFilter, searchQuery, currentOperator, entityTypes, userReferences]);
    
    const operatorsForCurrentFilter = useMemo(() => {
        if (!currentFilter) return OPERATORS;
        const config = getFilterConfig(currentFilter.key);
        const allowed = config?.allowedOperators;
        if (!allowed || !Array.isArray(allowed) || allowed.length === 0) return OPERATORS;
        return OPERATORS.filter((op) => allowed.includes(op.symbol));
    }, [currentFilter]);

    const filteredOperators = useMemo(() => {
        if (!searchQuery.trim()) return operatorsForCurrentFilter;
        return operatorsForCurrentFilter.filter(
            (op) => op.label.toLowerCase().includes(searchQuery.toLowerCase()) || op.symbol.includes(searchQuery)
        );
    }, [searchQuery, operatorsForCurrentFilter]);
    
    const formatDateRange = useCallback((value) => {
        if (!value) return '';
        if (value.relative && value.amount && value.unit && value.range) {
            const units = { m: 'minute(s)', h: 'hour(s)', d: 'day(s)', M: 'month(s)', Y: 'year(s)' };
            const ranges = { subtract: 'last', add: 'next' };
            return `${ranges[value.range] || value.range} ${value.amount} ${units[value.unit] || value.unit}`;
        }
        if (Array.isArray(value) && value.length === 2) {
            return `${moment(value[0]).format('MMM D, YYYY')} - ${moment(value[1]).format('MMM D, YYYY')}`;
        }
        return 'Date range selected';
    }, []);
    
    // Save search to recent searches
    const saveToRecentSearches = useCallback((tokens) => {
        if (!tokens?.length) return;
        
        const serialized = { tokens: tokens.map(t => ({
            filter: t.filter ? { key: t.filter.key, text: t.filter.text, icon: t.filter.icon } : null,
            operator: t.operator,
            value: t.value,
        })) };
        
        const searchStr = JSON.stringify(serialized.tokens);
        const isDuplicate = recentSearches.some(s => JSON.stringify(s.tokens) === searchStr);
        
        if (!isDuplicate) {
            dispatch(saveComponentState(COMPONENT_ID, { 
                recentSearches: [serialized, ...recentSearches].slice(0, MAX_RECENT_SEARCHES) 
            }));
        }
    }, [dispatch, recentSearches]);
    
    // Search execution
    const executeSearch = useCallback(async (tokens) => {
        if (!tokens?.length) return;
        
        lastLoadedIndexRef.current = 0; // Reset on new search (we're loading from 0)
        const { dataType, text, filterBy } = buildSearchCriteria(tokens);
        actions.setLoading();
        
        try {
            const data = await dispatch(executeGlobalSearch({
                startIndex: 0,
                stopIndex: constants.DEFAULT_PAGE_SIZE,
                dataType: dataType || undefined,
                text: text || undefined,
                filterBy: filterBy.length > 0 ? filterBy : undefined,
            }));
            
            actions.setResults(data.results || [], data.totalCount, data.hasMore);
            saveToRecentSearches(tokens);
        } catch (error) {
            console.error('Search failed:', error);
            actions.setError();
        }
    }, [dispatch, actions, constants.DEFAULT_PAGE_SIZE, saveToRecentSearches]);
    
    const loadMore = useCallback(async () => {
        if (!hasMore) return;
        
        const currentLength = searchResults.length;
        
        // Prevent duplicate requests for the same page
        if (lastLoadedIndexRef.current >= currentLength) return;
        lastLoadedIndexRef.current = currentLength;
        
        const { dataType, text, filterBy } = buildSearchCriteria(searchTokens);
        actions.setLoadingMore();
        
        try {
            const data = await dispatch(executeGlobalSearch({
                startIndex: currentLength,
                stopIndex: currentLength + constants.DEFAULT_PAGE_SIZE,
                dataType: dataType || undefined,
                text: text || undefined,
                filterBy: filterBy.length > 0 ? filterBy : undefined,
            }));
            actions.appendResults(data.results || [], data.totalCount, data.hasMore);
        } catch (error) {
            console.error('Load more failed:', error);
            // Reset on error so user can retry
            lastLoadedIndexRef.current = currentLength - 1;
        }
    }, [dispatch, actions, searchTokens, searchResults.length, hasMore, constants.DEFAULT_PAGE_SIZE]);
    
    // Event handlers
    const handleClose = useCallback(() => {
        lastLoadedIndexRef.current = -1;
        actions.reset();
        onClose();
    }, [actions, onClose]);
    
    const handleFilterClick = useCallback((filter) => {
        actions.selectFilter(filter);
        setTimeout(focusInput, 0);
    }, [actions, focusInput]);
    
    const handleOperatorClick = useCallback((symbol) => {
        // Capture currentFilter before ensureTypeTokenForPrimaryFilter clears it
        const filterToUse = currentFilter;
        
        if (isNullOperator(symbol) && filterToUse?.key?.startsWith('primary.')) {
            const config = getFilterConfig(filterToUse.key);
            if (config?.entityTypeUri) {
                ensureTypeTokenForPrimaryFilter(config.entityTypeUri);
            }
        }
        
        // Pass the filter explicitly so it's preserved even if state changed
        actions.selectOperator(symbol, filterToUse);
        setTimeout(focusInput, 0);
    }, [currentFilter, actions, focusInput, ensureTypeTokenForPrimaryFilter]);
    
    const handleValueClick = useCallback((value) => {
        if (!currentFilter) return;
        const config = getFilterConfig(currentFilter.key);
        if (currentFilter.key.startsWith('primary.') && config?.entityTypeUri) {
            ensureTypeTokenForPrimaryFilter(config.entityTypeUri);
        }
        const formatted = config?.formatSelectedValue ? config.formatSelectedValue(value) : value;
        actions.addToken(currentFilter, currentOperator, formatted);
        setTimeout(focusInput, 0);
    }, [currentFilter, currentOperator, actions, focusInput, ensureTypeTokenForPrimaryFilter]);
    
    const handleFilterInputComplete = useCallback((value) => {
        if (value && currentFilter) {
            actions.addToken(currentFilter, null, { dateRange: value, displayText: formatDateRange(value) });
        } else {
            actions.cancelFilter();
        }
        setTimeout(focusInput, 0);
    }, [currentFilter, actions, formatDateRange, focusInput]);
    
    const handleRecentClick = useCallback((recent) => {
        actions.loadRecent(recent.tokens);
        executeSearch(recent.tokens);
    }, [actions, executeSearch]);
    
    const handleFreeTextClick = useCallback((text) => {
        // Add free text token and execute search immediately
        const newTokens = [...searchTokens, { filter: null, operator: null, value: text }];
        actions.addFreeTextToken(text);
        executeSearch(newTokens);
        inputRef.current?.blur();
    }, [actions, searchTokens, executeSearch]);
    
    // Calculate total list items for keyboard navigation
    const getListItemCount = useCallback(() => {
        if (currentInputState === INPUT_STATE.OPERATOR) {
            return filteredOperators.length;
        }
        if (currentInputState === INPUT_STATE.VALUE && currentFilter) {
            return filteredValues.length;
        }
        // Filter list: "Search for text" (if query) + filters
        const hasFreeTextOption = searchQuery.trim();
        return (hasFreeTextOption ? 1 : 0) + filteredOptions.length;
    }, [currentInputState, currentFilter, searchQuery, filteredOptions.length, filteredValues.length, filteredOperators.length]);
    
    const handleKeyDown = useCallback((e) => {
        if (showFilterInput) return;
        
        const listCount = getListItemCount();
        
        // Arrow navigation: -1 means nothing highlighted
        // Down: -1 → 0 → 1 → ... → last → -1
        // Up: decrement, 0 → -1
        if (e.key === 'ArrowDown' && listCount > 0) {
            e.preventDefault();
            actions.setHighlightedIndex(highlightedIndex >= listCount - 1 ? -1 : highlightedIndex + 1);
            return;
        }
        
        if (e.key === 'ArrowUp' && highlightedIndex >= 0) {
            e.preventDefault();
            actions.setHighlightedIndex(highlightedIndex - 1);
            return;
        }
        
        if (e.key === 'Backspace' && searchQuery === '') {
            e.preventDefault();
            if (currentInputState === INPUT_STATE.VALUE || currentInputState === INPUT_STATE.OPERATOR) {
                actions.goBack();
            } else if (searchTokens.length > 0) {
                let lastRemovable = searchTokens.length - 1;
                while (lastRemovable >= 0 && lastRemovable === lockedTypeTokenIndex) {
                    lastRemovable -= 1;
                }
                if (lastRemovable >= 0) {
                    actions.removeToken(lastRemovable);
                }
            }
        }
        
        if (e.key === 'Enter') {
            // Only select highlighted item if something is actually highlighted
            if (highlightedIndex >= 0) {
                // Select highlighted item from list
                if (currentInputState === INPUT_STATE.OPERATOR && filteredOperators.length > 0) {
                    const operator = filteredOperators[highlightedIndex];
                    if (operator) {
                        handleOperatorClick(operator.symbol);
                        return;
                    }
                }
                
                if (currentInputState === INPUT_STATE.VALUE && currentFilter && filteredValues.length > 0) {
                    const value = filteredValues[highlightedIndex];
                    if (value) {
                        handleValueClick(value);
                        return;
                    }
                }
                
                if (currentInputState === INPUT_STATE.FILTER) {
                    const hasFreeTextOption = searchQuery.trim();
                    
                    // If highlighted index is 0 and we have free text option, do free text search
                    if (hasFreeTextOption && highlightedIndex === 0) {
                        handleFreeTextClick(searchQuery.trim());
                        return;
                    }
                    
                    // Otherwise select the filter (offset by 1 if free text option exists)
                    const filterIndex = hasFreeTextOption ? highlightedIndex - 1 : highlightedIndex;
                    const filter = filteredOptions[filterIndex];
                    if (filter) {
                        handleFilterClick(filter);
                        return;
                    }
                }
            }
            
            // Handle text input for filters with no predefined values (like entityPrimaryIndexes)
            if (currentInputState === INPUT_STATE.VALUE && currentFilter && searchQuery.trim()) {
                const config = getFilterConfig(currentFilter.key);
                // If filter allows text input, accept the typed value when Enter is pressed
                if (config?.allowTextInput) {
                    if (currentFilter.key.startsWith('primary.') && config?.entityTypeUri) {
                        ensureTypeTokenForPrimaryFilter(config.entityTypeUri);
                    }
                    const textValue = searchQuery.trim();
                    const formatted = config?.formatSelectedValue ? config.formatSelectedValue(textValue) : textValue;
                    // Build tokens array before dispatching (same pattern as handleFreeTextClick)
                    const newTokens = [...searchTokens, { filter: currentFilter, operator: currentOperator, value: formatted }];
                    actions.addToken(currentFilter, currentOperator, formatted);
                    executeSearch(newTokens);
                    inputRef.current?.blur();
                    return;
                }
            }
            
            // Fallback behaviors when no item is highlighted
            if (currentInputState === INPUT_STATE.FILTER && searchQuery.trim()) {
                // If no item highlighted but user typed something, add as free text
                handleFreeTextClick(searchQuery.trim());
                return;
            }
            
            // Execute search if we have tokens and no query
            if (searchQuery === '' && searchTokens.length > 0 && !currentFilter) {
                executeSearch(searchTokens);
                inputRef.current?.blur();
            } else if (searchQuery === '' && searchTokens.length === 0 && !currentFilter) {
                actions.reset();
            }
        }
    }, [
        showFilterInput, searchQuery, currentInputState, currentFilter, currentOperator, searchTokens,
        actions, filteredOptions, filteredValues, filteredOperators, executeSearch, highlightedIndex,
        getListItemCount, handleFilterClick, handleOperatorClick, handleValueClick, handleFreeTextClick,
        ensureTypeTokenForPrimaryFilter, lockedTypeTokenIndex,
    ]);
    
    const handleResultClick = useCallback((entity) => {
        history.push(getEntityUrl(entity.id, entity.type));
        handleClose();
    }, [handleClose]);
    
    const handleInputFocus = useCallback(() => {
        actions.setInputFocus(true);
        actions.setHighlightedIndex(-1); // Reset highlight when clicking in input
    }, [actions]);
    
    const handleInputBlur = useCallback(() => {
        // Delay to allow click events on filter list items to fire first.
        // Check if input is still blurred before updating — handles the race condition
        // where chip removal re-focuses the input before this timeout fires.
        setTimeout(() => {
            if (document.activeElement !== inputRef.current) {
                actions.setInputFocus(false);
            }
        }, 200);
    }, [actions]);
    
    const handleScroll = useCallback((e) => {
        if (!hasMore) return;
        const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight <= 100) {
            loadMore();
        }
    }, [loadMore, hasMore]);
    
    // Get search summary for no results
    const searchSummary = useMemo(() => 
        searchTokens.map(t => {
            const filterText = t.filter?.text || 'Search';
            const config = t.filter ? getFilterConfig(t.filter.key) : null;
            const displayValue = config?.formatDisplayValue ? config.formatDisplayValue(t.value) : (t.value?.displayName || t.value || '');
            return `${filterText} ${t.operator || ''} ${displayValue}`.trim();
        }).join(', ')
    , [searchTokens]);
    
    const placeholder = useMemo(() => {
        if (currentInputState === INPUT_STATE.OPERATOR) return 'Select operator...';
        if (currentInputState === INPUT_STATE.VALUE && currentFilter) {
            return `Search for ${currentFilter.text.toLowerCase()}...`;
        }
        return 'Search...';
    }, [currentInputState, currentFilter]);
    
    // Check if we should show filters overlay on top of results
    // Don't show overlay while loading more (pagination)
    const showFiltersOverlay = hasSearched && !isLoadingMore && (
        isInputFocused || 
        currentInputState !== INPUT_STATE.FILTER || 
        searchQuery.trim()
    );
    
    // Render content based on current state
    const renderContent = () => {
        // Date picker modal
        if (showFilterInput) {
            return (
                <GlobalSearchFilterInput
                    filter={currentFilter}
                    value={filterInputValue}
                    onChange={actions.setFilterInput}
                    onComplete={handleFilterInputComplete}
                    onCancel={() => { actions.cancelFilter(); setTimeout(focusInput, 0); }}
                />
            );
        }
        
        // Loading initial search
        if (isLoading) {
            return <NoResults><Loader size={40} /><p>Searching...</p></NoResults>;
        }
        
        // After search: show results with filter overlay when modifying
        if (hasSearched) {
            const resultsContent = totalCount > 0 ? (
                <SearchResults
                    results={searchResults}
                    totalCount={totalCount}
                    isLoadingMore={isLoadingMore}
                    entityTypes={entityTypes}
                    onResultClick={handleResultClick}
                    onScroll={handleScroll}
                />
            ) : (
                <NoSearchResults searchSummary={searchSummary} />
            );
            
            // Show filter overlay when user is modifying search
            if (showFiltersOverlay) {
                let overlayContent = null;
                
                if (currentInputState === INPUT_STATE.OPERATOR) {
                    overlayContent = (
                        <OperatorList
                            filterName={currentFilter?.text}
                            searchQuery={searchQuery}
                            onOperatorClick={handleOperatorClick}
                            highlightedIndex={highlightedIndex}
                            operators={operatorsForCurrentFilter}
                        />
                    );
                } else if (currentInputState === INPUT_STATE.VALUE && currentFilter) {
                    overlayContent = (
                        <FilterValueList
                            filterName={currentFilter.text.toLowerCase()}
                            values={filteredValues}
                            filterConfig={getFilterConfig(currentFilter.key)}
                            onValueClick={handleValueClick}
                            highlightedIndex={highlightedIndex}
                        />
                    );
                } else {
                    overlayContent = (
                        <FilterList
                            filters={filteredOptions}
                            searchQuery={searchQuery}
                            onFilterClick={handleFilterClick}
                            onFreeTextClick={handleFreeTextClick}
                            allFiltersUsed={filterOptions.length > 0 && filteredOptions.length === 0 && !searchQuery}
                            highlightedIndex={highlightedIndex}
                        />
                    );
                }
                
                return (
                    <>
                        <ResultsContainer $isHidden>{resultsContent}</ResultsContainer>
                        <FilterOverlay $isVisible>{overlayContent}</FilterOverlay>
                    </>
                );
            }
            
            return resultsContent;
        }
        
        // Operator selection (before first search)
        if (currentInputState === INPUT_STATE.OPERATOR) {
            return (
                <OperatorList
                    filterName={currentFilter?.text}
                    searchQuery={searchQuery}
                    onOperatorClick={handleOperatorClick}
                    highlightedIndex={highlightedIndex}
                    operators={operatorsForCurrentFilter}
                />
            );
        }
        
        // Value selection (before first search)
        if (currentInputState === INPUT_STATE.VALUE && currentFilter) {
            return (
                <FilterValueList
                    filterName={currentFilter.text.toLowerCase()}
                    values={filteredValues}
                    filterConfig={getFilterConfig(currentFilter.key)}
                    onValueClick={handleValueClick}
                    highlightedIndex={highlightedIndex}
                />
            );
        }
        
        // Initial/default state - show recent searches (if any) + filters
        // Hide recent searches when user starts typing
        return (
            <>
                {recentSearches.length > 0 && !searchQuery.trim() && (
                    <RecentSearches recentSearches={recentSearches} onRecentClick={handleRecentClick} />
                )}
                <FilterList
                    filters={filteredOptions}
                    searchQuery={searchQuery}
                    onFilterClick={handleFilterClick}
                    onFreeTextClick={handleFreeTextClick}
                    allFiltersUsed={filterOptions.length > 0 && filteredOptions.length === 0 && !searchQuery}
                    highlightedIndex={highlightedIndex}
                />
            </>
        );
    };
    
    return (
        <StyledDialog open={open} onClose={handleClose} onEntered={focusInput} maxWidth="md" fullWidth>
            <SearchInput
                ref={inputRef}
                searchQuery={searchQuery}
                searchTokens={searchTokens}
                currentFilter={currentFilter}
                currentOperator={currentOperator}
                currentInputState={currentInputState}
                placeholder={placeholder}
                onQueryChange={actions.setQuery}
                onKeyDown={handleKeyDown}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onRemoveToken={handleRemoveToken}
                nonRemovableTokenIndices={lockedTypeTokenIndex != null ? [lockedTypeTokenIndex] : []}
                onClose={handleClose}
                onContainerClick={handleInputClick}
            />
            <SearchContent $hasResults={hasSearched && totalCount > 0}>
                {renderContent()}
            </SearchContent>
        </StyledDialog>
    );
};

GlobalSearchModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default GlobalSearchModal;
